import { useState, useEffect, useCallback, useMemo } from 'react';
import { api } from '../../services/api';
import { toast } from 'sonner';
import { CalendarBlank } from '@phosphor-icons/react';

const DAYS = [
  { key: 0, label: 'Lunedì', short: 'Lun' },
  { key: 1, label: 'Martedì', short: 'Mar' },
  { key: 2, label: 'Mercoledì', short: 'Mer' },
  { key: 3, label: 'Giovedì', short: 'Gio' },
  { key: 4, label: 'Venerdì', short: 'Ven' },
];

// Slot da 15 min, 13:00 -> 20:00 (esclusa) = 28 righe
function buildSlots() {
  const slots = [];
  for (let mins = 13 * 60; mins < 20 * 60; mins += 15) {
    const h = Math.floor(mins / 60);
    const m = mins % 60;
    slots.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
  }
  return slots;
}

function toMin(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

// Palette compatta per differenziare gli alunni (index-based)
const STUDENT_COLORS = [
  '#DBEAFE', '#FCE7F3', '#DCFCE7', '#FEF3C7', '#EDE9FE',
  '#FEE2E2', '#CCFBF1', '#FED7AA', '#E9D5FF', '#BAE6FD',
];

export function OrarioGiornoSection({ anno }) {
  const [day, setDay] = useState(0);
  const [loading, setLoading] = useState(false);
  const [teachers, setTeachers] = useState([]);
  const [lessons, setLessons] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.getLessonsDayView(day, anno);
      setTeachers(res.data.teachers || []);
      setLessons(res.data.lessons || []);
    } catch {
      toast.error('Errore caricamento orario giornaliero');
    } finally {
      setLoading(false);
    }
  }, [day, anno]);

  useEffect(() => { load(); }, [load]);

  const slots = useMemo(buildSlots, []);

  // Pre-calcolo mappa (teacherId + slot) -> {student_name, is_start, is_prova, studentIdx, span}
  // Per ogni lezione: individua slot iniziale e span (numero di celle da 15 min occupate)
  const cellMap = useMemo(() => {
    const map = {};
    // Genera un indice colore stabile per alunno (per teacher)
    const studentIdxByTeacher = {};
    for (const l of lessons) {
      const startM = toMin(l.start_time);
      const endM = toMin(l.end_time);
      if (startM == null || endM == null || endM <= startM) continue;
      const startSlotIdx = Math.floor((startM - 13 * 60) / 15);
      const spanSlots = Math.max(1, Math.ceil((endM - startM) / 15));
      if (!studentIdxByTeacher[l.teacher_id]) studentIdxByTeacher[l.teacher_id] = {};
      const seen = studentIdxByTeacher[l.teacher_id];
      if (!(l.student_id in seen)) seen[l.student_id] = Object.keys(seen).length;
      const studentIdx = seen[l.student_id];
      for (let i = 0; i < spanSlots; i++) {
        const slotIdx = startSlotIdx + i;
        if (slotIdx < 0 || slotIdx >= slots.length) continue;
        const key = `${l.teacher_id}|${slotIdx}`;
        map[key] = {
          student_name: l.student_name,
          is_start: i === 0,
          is_prova: l.is_prova,
          studentIdx,
          rowSpan: i === 0 ? Math.min(spanSlots, slots.length - startSlotIdx) : 0,
          start_time: l.start_time,
          end_time: l.end_time,
        };
      }
    }
    return map;
  }, [lessons, slots]);

  return (
    <>
      <div className="mb-6 animate-fade-in flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold font-['Work_Sans'] tracking-tight mb-2">Orario Giorno</h1>
          <p className="text-[#52525B] text-sm">Vista giornaliera per fasce da 15 min · colonne = insegnanti, righe = orari</p>
        </div>
      </div>

      {/* Filtro giorno */}
      <div className="flex items-center gap-1 mb-4 flex-wrap animate-fade-in animate-delay-1" data-testid="orario-giorno-day-tabs">
        {DAYS.map(d => (
          <button
            key={d.key}
            type="button"
            onClick={() => setDay(d.key)}
            className={`px-4 py-2 rounded-sm text-sm font-semibold uppercase tracking-wider transition-colors ${
              day === d.key ? 'bg-[#2D2A4A] text-white' : 'bg-white border border-[#E4E4E7] text-[#52525B] hover:bg-[#FAFAFA]'
            }`}
            data-testid={`orario-giorno-day-${d.key}`}
          >
            {d.short}
          </button>
        ))}
        <div className="ml-auto text-xs text-[#52525B]">
          {teachers.length} {teachers.length === 1 ? 'insegnante' : 'insegnanti'} · {lessons.length} {lessons.length === 1 ? 'lezione' : 'lezioni'}
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#52525B]">Caricamento...</div>
      ) : teachers.length === 0 ? (
        <div className="text-center py-12 bg-white border border-[#E4E4E7] rounded-sm">
          <CalendarBlank size={48} className="mx-auto text-[#E4E4E7] mb-4" />
          <p className="text-[#52525B]">Nessuna lezione registrata per {DAYS.find(d => d.key === day)?.label} in questo anno accademico.</p>
        </div>
      ) : (
        <div className="animate-fade-in animate-delay-2" data-testid="orario-giorno-grid-wrapper">
          <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
            <table className="w-full border-collapse text-xs table-fixed" data-testid="orario-giorno-table">
              <thead>
                <tr className="bg-[#2D2A4A] text-white">
                  <th className="px-2 py-2 text-left font-semibold uppercase tracking-wider border-r border-[#3D3A5A]" style={{ width: '70px' }}>Orario</th>
                  {teachers.map(t => (
                    <th key={t.id} className="px-2 py-2 text-left font-semibold uppercase tracking-wider border-r border-[#3D3A5A]" title={`${t.name} · ${t.instrument || '—'}`}>
                      <div className="font-semibold truncate">{t.name}</div>
                      {t.instrument && <div className="text-[10px] font-normal opacity-80 truncate">{t.instrument}</div>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {slots.map((s, sIdx) => {
                  const isHourStart = s.endsWith(':00');
                  return (
                    <tr key={s} className={isHourStart ? 'border-t-2 border-[#2D2A4A]/30' : 'border-t border-[#F4F4F5]'}>
                      <td className={`px-2 py-1 border-r border-[#E4E4E7] font-mono ${isHourStart ? 'font-bold text-[#2D2A4A]' : 'text-[#A1A1AA]'}`} data-testid={`slot-${s}`}>{s}</td>
                      {teachers.map(t => {
                        const cell = cellMap[`${t.id}|${sIdx}`];
                        if (!cell) {
                          return <td key={t.id} className="border-r border-[#F4F4F5] px-1 py-1 h-7"></td>;
                        }
                        if (!cell.is_start) return null;
                        const bg = STUDENT_COLORS[cell.studentIdx % STUDENT_COLORS.length];
                        return (
                          <td
                            key={t.id}
                            rowSpan={cell.rowSpan}
                            className="border-r border-[#E4E4E7] px-2 py-1 align-middle"
                            style={{ backgroundColor: bg }}
                            title={`${cell.student_name} · ${cell.start_time}–${cell.end_time}${cell.is_prova ? ' (PROVA)' : ''}`}
                            data-testid={`cell-${t.id}-${sIdx}`}
                          >
                            <div className="font-mono text-[10px] font-bold text-[#2D2A4A] tracking-tight leading-tight">
                              {cell.start_time}<span className="opacity-60">–</span>{cell.end_time}
                            </div>
                            <div className="font-semibold text-[#0A0A0A] uppercase truncate text-[11px] leading-tight">
                              {cell.student_name}
                            </div>
                            {cell.is_prova && (
                              <div className="inline-block mt-0.5 px-1 py-0.5 bg-amber-500 text-white text-[8px] font-extrabold uppercase tracking-wider rounded">PROVA</div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}
